<template>
    <div class="w-full">
        <!-- Toggle -->
        <button type="button" @click="toggleAccordion" class="flex items-center justify-between w-full" :class="toggleClass"
            :aria-expanded="isOpen" :aria-controls="`collapse${_uid}`">
            <slot name="title" />
            <div class="ml-auto">
                <svg class="w-3 transition-transform duration-300 transform" :class="{ 'rotate-180': isOpen }" fill="none"
                    stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 10" aria-hidden="true">
                    <path d="M15 1.2l-7 7-7-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </div>
        </button>

        <!-- Animated collapsible body -->
        <div ref="body" :id="`collapse${_uid}`" class="overflow-hidden transition-all duration-300 ease-in-out"
            :style="{ maxHeight: isOpen ? bodyHeight + 'px' : '0px' }"
            :class="[isOpen ? 'opacity-100' : 'opacity-0', bodyClass]">
            <slot name="content" />
        </div>
    </div>
</template>

<script>
import { nextTick } from "vue";

export default {
    name: "AppAccordion",
    props: {
        startOpenMobile: {
            type: Boolean,
            default: false,
        },
        open: {
            type: Boolean,
            default: false,
        },
        toggleClass: {
            type: String,
            default: "",
        },
        bodyClass: {
            type: String,
            default: "",
        },
        group: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            isOpen: false,
            bodyHeight: 0,
        };
    },
    async mounted() {
        await nextTick();
        this.measureHeight();

        const isMobile = window.innerWidth < 768;
        if (this.open || (this.startOpenMobile && isMobile)) {
            this.isOpen = true;
            await nextTick();
            this.measureHeight();
        }

        if (this.group) {
            window.addEventListener("accordion:open", this.handleGroupEvent);
        }

        window.addEventListener("resize", this.measureHeight);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.measureHeight);
        if (this.group) {
            window.removeEventListener("accordion:open", this.handleGroupEvent);
        }
    },
    methods: {
        async measureHeight() {
            await nextTick(); // ensure slot content rendered
            if (this.$refs.body) {
                this.bodyHeight = this.$refs.body.scrollHeight;
            }
        },
        async toggleAccordion() {
            if (this.group && !this.isOpen) {
                window.dispatchEvent(
                    new CustomEvent("accordion:open", {
                        detail: { group: this.group, uid: this._uid },
                    })
                );
            }
            this.isOpen = !this.isOpen;

            await this.$nextTick();
            this.measureHeight();

            if (this.isOpen) {
                this.$emit("opened", { uid: this._uid, group: this.group });
            } else {
                this.$emit("closed", { uid: this._uid, group: this.group });
            }
        },
        handleGroupEvent(e) {
            const { group, uid } = e.detail;
            if (group === this.group && uid !== this._uid) {
                this.isOpen = false;
            }
        },
        async openAccordion() {
            if (this.group) {
                window.dispatchEvent(
                    new CustomEvent("accordion:open", {
                        detail: { group: this.group, uid: this._uid },
                    })
                );
            }
            this.isOpen = true;
            await nextTick();
            this.measureHeight();
        },
        closeAccordion() {
            this.isOpen = false;
        },
    },
    expose: ["openAccordion", "closeAccordion", "toggleAccordion"],
};
</script>
